import { type ReactNode, useState } from 'react';
import { api, ApiError, type User } from '@/api/client';
import { Brand } from './Brand';
import { PASSWORD_MIN, passwordProblem } from './PasswordDialog';

// The first thing after signing in with a password someone else set: one's own, before anything else opens (D-29).
export function ChoosePassword(props: { user: User; onChosen: (user: User) => void; onSignOut: () => void }): ReactNode {
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [again, setAgain] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const problem = passwordProblem(next, again);

    return (
        <div className="login">
            <form
                className="float"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (problem || !current || !next || next !== again) {
                        return;
                    }
                    setBusy(true);
                    setError(null);
                    api.changePassword(current, next)
                        .then(({ user }) => props.onChosen(user))
                        .catch((reason: unknown) => setError(reason instanceof ApiError ? reason.message : 'The server did not answer'))
                        .finally(() => setBusy(false));
                }}
            >
                <Brand />
                <h1>Choose your password</h1>
                <p className="lead">
                    Welcome, {props.user.name || props.user.email}. The password you were given was for once: pick your own, {PASSWORD_MIN} characters
                    or more.
                </p>
                {/* So a password manager keeps the new password under the right account. */}
                <input type="email" autoComplete="username" value={props.user.email} readOnly hidden />
                <label>
                    <span className="micro">The password you were given</span>
                    <input
                        type="password"
                        autoComplete="current-password"
                        value={current}
                        onChange={(event) => setCurrent(event.target.value)}
                        autoFocus
                    />
                </label>
                <label>
                    <span className="micro">Your new password</span>
                    <input type="password" autoComplete="new-password" value={next} onChange={(event) => setNext(event.target.value)} />
                </label>
                <label>
                    <span className="micro">Again</span>
                    <input type="password" autoComplete="new-password" value={again} onChange={(event) => setAgain(event.target.value)} />
                </label>
                {(problem || error) && <div className="error">{problem ?? error}</div>}
                <button className="submit" disabled={busy || Boolean(problem) || !current || !next || next !== again}>
                    {busy ? 'Saving…' : 'Save and continue'}
                </button>
                <button type="button" className="aside" onClick={props.onSignOut}>
                    Sign out
                </button>
            </form>
        </div>
    );
}
